<template>
    <modal
        v-if="show"
        :form="form"
        close-on-success
        type="is-primary"
        size="is-small"
        :title="titleModal"
        :submit-text="titleButtonModal"
        cancel-text="Cancel"
        class="tagger-modal"
        @closed="handleClose"
        @submit="save"
    >
        <div class="field tag-search">
            <p ref="stickyScroll" class="control has-icons-left">
                <fa-icon
                    :icon="searching ? 'circle-notch' : 'search'"
                    :spin="searching"
                    class="is-left"
                    :class="{
                        'has-text-primary': searching,
                    }"
                />
                <span
                    v-for="selected in form.tags"
                    :key="selected.id"
                    class="tag is-small"
                    :class="{
                        'is-success': deletingId !== selected.id,
                        'is-danger': deletingId === selected.id,
                    }"
                >
                    {{ selected.label }}
                    <button class="delete is-small" @click.prevent="removeTag(selected)"></button>
                </span>

                <span v-if="errorMessage" class="help has-text-danger">
                    {{ errorMessage }}
                </span>

                <input
                    ref="search"
                    v-model="search"
                    class="input"
                    type="text"
                    placeholder="Search tags..."
                    @input="changeInput"
                />
            </p>
        </div>
        <div class="tag-bar-container">
            <div
                v-for="(tag, i) in filteredTags"
                :key="tag.id"
                class="tag-bar"
                :class="{
                    highlighted: highlightedIndex === i,
                }"
                @click="selectTag(tag)"
            >
                <div class="tag-label" v-text="tag.label" />
            </div>
            <div
                v-if="shouldShowCreate"
                :key="-2"
                class="tag-bar is-create"
                :class="{
                    highlighted: highlightedIndex === 0 && filteredTags.length === 0,
                }"
                @click="createTag"
            >
                <span class="tag-label">
                    <fa-icon icon="tag" />Create new tag: "{{ search }}"
                </span>
            </div>
        </div>
    </modal>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import Form from 'core/Form';
import stickyScroll from 'mixins/sticky-scroll';
import tags from 'mixins/tags';

export default {
    emits: ['closed', 'tagged'],
    
    mixins: [stickyScroll, tags],

    props: {
        show: { type: Boolean, default: false },
        contact: { type: [Object, Boolean], default: false },
        bulk: { type: Object },
        bulkAdd: { type: Boolean, default: true },
        target: { type: String, default: '' },
    },

    data: () => ({
        search: '',
        searching: false,
        searchingTimeout: null,
        deletingId: null,
        isLoadingTags: true,
        highlightedIndex: -1,
        savingCanceller: null,
        errorMessage: null,
        form: new Form(
            {
                tags: [],
                tag: null,
                contact_ids: null,
                label: '',
                bulk_all: false,
                remove_all: false,
                filtersList: [],
            },
            false
        ),
    }),

    computed: {
        titleModal() {
            if (this.hasTargetContacts) {
                if (this.bulkAdd) {
                    return 'Add Tag';
                }

                return 'Remove Tag';
            }

            return 'Tag Contact';
        },

        titleButtonModal() {
            if (this.hasTargetContacts) {
                if (this.bulkAdd) {
                    return 'Add';
                }

                return 'Remove';
            }

            return 'Save';
        },

        hasTargetContacts() {
            return this.target === 'contacts';
        },

        shouldShowCreate() {
            return (
                this.tags !== null &&
                this.search.length &&
                this.filteredTags.findIndex(
                    (o) => o.label.toLowerCase() === this.search.toLowerCase()
                ) === -1 &&
                this.form.tags.findIndex(
                    (o) => o.label.toLowerCase() === this.search.toLowerCase()
                ) === -1
            );
        },

        unselectedTags() {
            if (!this.tags) {
                return [];
            }

            return this.tags.filter((tag) => {
                return !this.form.tags.find((o) => o.id === tag.id);
            });
        },

        filteredTags() {
            if (!this.tags) {
                return [];
            }

            if (this.search === '') {
                return this.unselectedTags;
            }

            return this.unselectedTags.filter((tag) => {
                return (
                    tag.label.toLowerCase().indexOf(this.search.toLowerCase()) !== -1 ||
                    tag.label.toLowerCase().indexOf(this.search.toLowerCase()) !== -1
                );
            });
        },

        lastSelectedTag() {
            return this.form.tags[this.form.tags.length - 1];
        },

        ...mapState(stores.Tags, ['tagsPagination']),
    },

    watch: {
        show(opened, wasOpened) {
            if (opened) {
                this.handleShown();
            }

            if (!opened && wasOpened) {
                this.handleHidden();
            }
        },

        search(search) {
            if (this.searchingTimeout) {
                clearTimeout(this.searchingTimeout);
            }

            if (!this.search) {
                this.searching = false;
                this.$store.dispatch('tagsClearSearch');
                return;
            }

            if (this.search.length > 1 && this.highlightedIndex === -1) {
                this.highlightedIndex = 0;
            }

            this.searchingTimeout = setTimeout(() => {
                this.searching = true;

                this.$store.dispatch('tagsSearch', search).then(() => {
                    this.searching = false;
                    this.searchingTimeout = null;
                });
            }, 300);
        },

        'form.tags'(tags) {
            if (tags && this.show) {
                setTimeout(() => {
                    this.stickyScroll(true);
                }, 20);
            }
        },
    },

    methods: {
        handleClose() {
            this.errorMessage = null;
            this.$emit('closed');
        },

        changeInput(e) {
            if (e.currentTarget.value.trim()) {
                this.errorMessage = null;
            }
        },

        async handleShown() {
            document.addEventListener('keydown', this.handleKeyDown);

            try {
                await this.$store.dispatch('resetTagsParameters', {
                    length: 1000,
                });
                await this.$store.dispatch('tagsLoad');

                if (this.tags === null) {
                    this.$nextTick(() => {
                        this.$refs['search'].focus();
                    });
                }

                if (this.contact) {
                    if (this.contact.tags.length) {
                        this.form.tags = [];

                        this.contact.tags.forEach((o) => this.form.tags.push(o));
                    } else {
                        this.form.tags = [];
                    }
                }
            } finally {
                this.isLoadingTags = false;
            }
        },

        handleHidden() {
            this.search = '';
            this.form.reset();
            this.form.tags = [];

            // Reset form tags to contact's existing tags..
            if (this.contact && this.contact.tags) {
                this.contact.tags.forEach((o) => this.form.tags.push(o));
            }

            document.removeEventListener('keydown', this.handleKeyDown);
        },

        handleKeyDown(e) {
            switch (e.keyCode) {
            case 40:
                this.arrowDown();
                break;
            case 38:
                this.arrowUp();
                break;
            case 27:
                this.deleteDown();
                break;
            case 8:
                this.deleteDown();
                break;
            case 13:
                e.preventDefault();
                this.enterDown();
                break;
            }
        },

        selectTag(tag) {
            this.highlightedIndex = -1;

            if (!this.form.tags.find((o) => o.id === tag.id)) {
                this.form.tags.push(tag);
            }

            this.search = '';
            this.$refs['search'].focus();
        },

        createTag() {
            const form = new Form({
                label: this.search,
            });

            this.$store
                .dispatch('createTags', { form: form })
                .then((response) => {
                    this.form.tags.push(response);

                    this.highlightedIndex = -1;
                    this.search = '';
                    this.$refs['search'].focus();
                    this.errorMessage = null;
                })
                .catch(() => {
                    this.errorMessage = form.errors.get('label');
                });
        },

        removeTag(tag) {
            this.highlightedIndex = -1;
            this.deletingId = null;

            this.form.tags = this.form.tags.filter((o) => o.id !== tag.id);
        },

        deleteDown() {
            this.highlightedIndex = -1;

            if (this.search === '' && this.form.tags.length) {
                if (this.deletingId === this.lastSelectedTag.id) {
                    this.removeTag(this.lastSelectedTag);
                } else {
                    this.deletingId = this.lastSelectedTag.id;
                }
            }
        },

        arrowDown() {
            if (this.highlightedIndex < this.filteredTags.length - 1) {
                ++this.highlightedIndex;
            }
        },

        arrowUp() {
            if (this.highlightedIndex > 0) {
                --this.highlightedIndex;
            }
        },

        enterDown() {
            if (
                this.highlightedIndex === -1 ||
                (this.highlightedIndex === 0 && !this.shouldShowCreate)
            ) {
                return;
            }

            if (this.highlightedIndex === 0 && this.filteredTags.length === 0) {
                const form = new Form({
                    label: this.search,
                });

                this.$store
                    .dispatch('createTags', { form: form })
                    .then((response) => {
                        this.form.tags.push(response);
                        this.selectNextTag();

                        this.errorMessage = null;
                    })
                    .catch(() => {
                        this.errorMessage = form.errors.get('label');
                    });
            } else {
                let tagToAdd = Object.assign({}, this.filteredTags[this.highlightedIndex]);
                this.form.tags.push(tagToAdd);
                this.selectNextTag();
            }
        },

        escDown() {
            this.highlightedIndex = -1;

            if (this.deletingId) {
                this.deletingId = null;
            }
        },

        selectNextTag() {
            this.search = '';
            this.$refs['search'].focus();
            this.highlightedIndex = this.unselectedTags.length ? 0 : -1;
        },

        save() {
            this.$emit('tagged', this.form);

            this.errorMessage = null;
        },
    },
};
</script>

<style scoped>
.tag-search {
    padding-left: 0.15rem;
}

.tag-search .control {
    position: relative;
    padding-left: 2.25em;
    display: flex;
    flex-wrap: wrap;
    border-bottom: 1px solid #dbdbdb;
    align-items: center;
    padding-right: 0.625em;
    max-height: 85px;
    overflow-y: scroll;
}

.tag-search .input {
    border: 0;
    background: none;
    box-shadow: none;
    flex-grow: 1;
    width: auto;
    min-width: 100px;
    padding-left: 0;
}

.tag-search span.icon {
    width: 2.25rem;
}

.tag-search .tag {
    margin-right: 0.25rem;
    margin-top: 5.5px;
    margin-bottom: 5px;
}

.tag-search .tag:last-of-type {
    margin-right: 0.5rem;
}

.tag-bar-container {
    height: 250px;
    max-height: 250px;
    overflow-y: scroll;
}

.tag-label {
    display: flex;
    align-items: center;
}

.tag-label .icon {
    margin-right: 5px;
}

.tag-bar {
    padding: 0.5rem 0.75rem !important;
}

.tag-bar.is-create {
    padding-left: 0.5rem !important;
}
</style>
